import React from "react";
import { Copy, Mail, AlertTriangle, CheckCircle, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Generic E-Transfer instructions panel for ANY payment type.
 * Props:
 *   payment: { amount, client_email, reference_id, label?, title?, subtitle?, type? }
 *   site:    { etransfer_email, etransfer_instructions }
 *   onClose: () => void
 */
export default function ETransferPanel({ payment, site, onClose }) {
  if (!payment || !site) return null;
  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const amount = payment.amount || 0;
  const clientEmail = payment.client_email || payment.email || "";
  const reference = payment.reference_id || payment.id || "";
  const title = payment.title || (payment.type === "subscription"
    ? "Activate your membership"
    : payment.type === "unlock"
    ? "Unlock this content"
    : "Send your deposit");
  const subtitle = payment.subtitle || (payment.type === "subscription"
    ? "Your membership is reserved. Send your payment via Interac e-Transfer and Layla will activate your access as soon as it arrives."
    : payment.type === "unlock"
    ? "Send your payment via Interac e-Transfer and Layla will unlock this content for you as soon as it arrives."
    : "Your rendezvous is reserved. To lock it in, please send your non-refundable deposit via Interac e-Transfer. Once received, Layla will personally confirm your booking by email.");

  return (
    <div className="fixed inset-0 z-[90] bg-noir-base/85 backdrop-blur-md flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid="etransfer-panel">
      <div className="glass-panel rounded-lg max-w-xl w-full my-8 relative border-pink-hot/40 glow-pink">
        <button onClick={onClose} className="absolute top-4 right-4 text-champagne/60 hover:text-pink-hot" data-testid="etransfer-close">
          <X className="w-5 h-5" />
        </button>

        <div className="p-8 sm:p-10">
          <div className="flex items-center gap-3 mb-3">
            <CheckCircle className="w-6 h-6 text-pink-hot" />
            <div className="text-[10px] tracking-[0.3em] uppercase text-pink-blush">Final step · e-Transfer</div>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl text-champagne leading-tight mb-2">
            {title.split(" ").slice(0, -1).join(" ")} <span className="italic text-pink-hot">{title.split(" ").slice(-1)}</span>
          </h2>
          <p className="text-sm text-champagne/75 leading-relaxed mb-6">{subtitle}</p>

          {/* Amount */}
          <div className="bg-noir-base/60 border border-pink-hot/30 rounded-md p-5 mb-4">
            <div className="text-[10px] tracking-[0.3em] uppercase text-rosegold mb-1">
              Amount {payment.label ? `· ${payment.label}` : ""}
            </div>
            <div className="flex items-end justify-between gap-3">
              <div className="font-display text-5xl text-pink-hot leading-none">${Number(amount).toFixed(0)}</div>
              <button
                onClick={() => copy(Number(amount).toFixed(2), "Amount")}
                className="text-xs text-champagne/70 hover:text-pink-hot flex items-center gap-1"
                data-testid="copy-amount"
              >
                <Copy className="w-3 h-3" /> Copy
              </button>
            </div>
            {payment.balance_due > 0 && (
              <div className="text-xs text-champagne/60 mt-2">Balance ${payment.balance_due.toFixed(0)} due in person</div>
            )}
          </div>

          {/* E-Transfer email */}
          <div className="bg-noir-base/60 border border-pink-hot/30 rounded-md p-5 mb-4">
            <div className="text-[10px] tracking-[0.3em] uppercase text-rosegold mb-1">Send e-Transfer to</div>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 font-display text-xl sm:text-2xl text-champagne break-all" data-testid="etransfer-email">
                <Mail className="w-5 h-5 text-pink-hot shrink-0" />
                {site.etransfer_email}
              </div>
              <button
                onClick={() => copy(site.etransfer_email, "E-Transfer email")}
                className="text-xs text-champagne/70 hover:text-pink-hot flex items-center gap-1 whitespace-nowrap"
                data-testid="copy-email"
              >
                <Copy className="w-3 h-3" /> Copy
              </button>
            </div>
          </div>

          {/* Critical: message field */}
          <div className="bg-pink-hot/10 border border-pink-hot/50 rounded-md p-5 mb-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-pink-hot shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-[10px] tracking-[0.3em] uppercase text-pink-hot mb-2">Important — message field</div>
                <p className="text-sm text-champagne/90 leading-relaxed mb-3">
                  In the <strong>message</strong> section of the e-Transfer, include <strong className="text-pink-hot">ONLY your email address</strong>. Nothing else — no name, no notes, no references.
                </p>
                {clientEmail && (
                  <div className="bg-noir-base/60 border border-pink-hot/20 rounded-sm p-3 flex items-center justify-between gap-3">
                    <code className="text-sm text-pink-blush break-all" data-testid="message-email-example">{clientEmail}</code>
                    <button
                      onClick={() => copy(clientEmail, "Your email")}
                      className="text-xs text-champagne/70 hover:text-pink-hot flex items-center gap-1 whitespace-nowrap"
                      data-testid="copy-my-email"
                    >
                      <Copy className="w-3 h-3" /> Copy
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {site.etransfer_instructions && (
            <div className="text-sm text-champagne/75 leading-relaxed mb-6">
              {site.etransfer_instructions}
            </div>
          )}

          {reference && (
            <div className="border-t border-pink-hot/15 pt-4 text-xs text-champagne/60 space-y-1">
              <div>Ref: <span className="text-champagne/90 font-mono">{String(reference).slice(0, 8).toUpperCase()}</span></div>
              {payment.details && <div>{payment.details}</div>}
            </div>
          )}

          <button
            onClick={onClose}
            className="w-full mt-6 py-4 bg-pink-hot text-white text-xs tracking-[0.3em] uppercase rounded-sm hover:bg-pink-glow glow-pink transition-all"
            data-testid="etransfer-done"
          >
            I've sent the e-Transfer
          </button>
          <p className="text-[11px] text-center text-champagne/50 mt-3">
            Layla will confirm as soon as the e-Transfer lands in her inbox.
          </p>
        </div>
      </div>
    </div>
  );
}
